import { useMemo } from 'react'

import { useIntl } from 'react-intl'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'
import { convertTZ, useIntlDateFormatter } from 'lib/datetime/dateUtils'

import { useCruiseOfferInclusions } from 'hooks/Cruise/useCruiseOfferInclusions'

import BodyText from 'LuxKit/Typography/BodyText'
import Divider from 'LuxKit/Divider'
import Heading from 'LuxKit/Typography/Heading'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import CruiseLuxPlusLabel from 'components/Cruises/CruiseLuxPlusLabel'
import CruiseLoyaltyPoints from 'components/Cruises/CruiseLoyaltyPoints'
import CruiseLuxPlusOnboardCreditLabel from 'components/Cruises/CruiseInclusion/CruiseLuxPlusOnboardCreditLabel'

import CruiseDepartureTilePrices from './CruiseDepartureTilePrices'
import CruiseStandardInclusionLabel from 'components/Cruises/CruiseInclusion/CruiseStandardInclusionLabel'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { useCruiseMemberPricing } from 'hooks/Cruise/useCruiseMemberPricing'
import AddToWeddingGiftRegistryCartCTA from 'weddings/components/WeddingAddToGiftRegistryCartCTA'
import useOfferPageBurnPoints from 'hooks/useOfferPageBurnPoints'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { isCruiseDepartureSoldOut } from 'lib/cruises/cruiseDeparturePricing'

interface Props {
  offer: App.CruiseOffer
  departure: App.CruiseDepartureDetails
  onHandleDepartureModal: (departure: App.CruiseDepartureDetails) => void
  isWeddingGiftRegistry?: boolean
}

function CruiseDepartureTile({
  offer,
  departure,
  onHandleDepartureModal,
  isWeddingGiftRegistry = false,
}: Props) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const { standardInclusions, luxPlusInclusions } = useCruiseOfferInclusions({ departure })
  const { departureMemberPricingByCategory, canSeeLuxPlusLabel } = useCruiseMemberPricing(offer, departure)

  const isDepartureSoldOut = isCruiseDepartureSoldOut(
    offer.lowestDeparturePricesByCategory,
    departure.id,
  )

  const loyaltyProductType = useMemo(() => getLoyaltyProductType(offer), [offer])
  const bookingPrice = departure.lowestOverallPriceDetails.total
  const burnResult = useOfferPageBurnPoints(
    bookingPrice,
    loyaltyProductType ?? 'cruise_group_3',
    undefined,
    { disabled: !loyaltyProductType || !bookingPrice },
  )
  const hasInclusions = [
    luxPlusInclusions.length,
    standardInclusions.length,
    offer.evergreenInclusions?.length,
  ].some(Boolean)

  const {
    departureLabel,
    departureWeekdayLabel,
    arrivalLabel,
    arrivalWeekdayLabel,
  } = useMemo(() => {
    // Departure and Arrival dates are related to the port timezone, but the timezone is not provided.
    // To enforce the date to stay the same even in negative timezones (such as Americas), we convert the date to UTC
    const departureDate = convertTZ(new Date(departure.departureDate!), 'UTC')
    const arrivalDate = convertTZ(new Date(departure.arrivalDate!), 'UTC')

    return {
      departureLabel: intlDateFormatter(departureDate, INTL_DMY_CASUAL_FORMAT),
      departureWeekdayLabel: intlDateFormatter(departureDate, INTL_LONG_WEEKDAY),
      arrivalLabel: intlDateFormatter(arrivalDate, INTL_DMY_CASUAL_FORMAT),
      arrivalWeekdayLabel: intlDateFormatter(arrivalDate, INTL_LONG_WEEKDAY),
    }
  }, [departure.departureDate, departure.arrivalDate, intlDateFormatter])

  return <>
    <Group
      direction="vertical"
      desktopDirection="horizontal"
      gap={20}
      horizontalAlign="space-between"
      verticalAlign="center"
    >
      <Group direction="vertical" gap={12}>
        <Group direction="horizontal" gap={12}>
          <div>
            <Heading variant="heading4" wrap="no-wrap">
              {departureLabel}
            </Heading>
            <BodyText variant="small" weight="normal" colour="light-foreground-alt">
              {departureWeekdayLabel}
            </BodyText>
          </div>

          <LineArrowRightIcon />

          <div>
            <Heading variant="heading4" wrap="no-wrap">
              {arrivalLabel}
            </Heading>
            <BodyText variant="small" weight="normal" colour="light-foreground-alt">
              {arrivalWeekdayLabel}
            </BodyText>
          </div>
        </Group>

        {hasInclusions && <Group
          gap={8}
          direction="vertical"
          desktopGap={16}
          horizontalAlign="start"
        >
          <CruiseStandardInclusionLabel
            standardInclusions={standardInclusions}
            allInclusions
          />

          <CruiseLuxPlusOnboardCreditLabel
            luxPlusInclusions={luxPlusInclusions}
            offer={offer}
            isModalPrimaryActionHidden
          />
        </Group>}

        <PriceRowAgentHubCommission
          checkIn={departure.departureDate ?? undefined}
          checkOut={departure.arrivalDate ?? undefined}
          optionId={departure.id ?? undefined}
          offerId={offer.id}
          offer={offer}
          productType={offer.productType}
        />

        {canSeeLuxPlusLabel && <CruiseLuxPlusLabel
          isModalEnabled
          offer={offer}
        />}

        <CruiseLoyaltyPoints
          offer={offer}
          departureId={departure.id}
          bookingPrice={bookingPrice}
          calculationType="estimate"
          pointsToApply={burnResult.pointsToBurn}
          burnPriceInPoints={burnResult.priceInPoints}
        />
      </Group>

      <CruiseDepartureTilePrices
        offer={offer}
        departure={departure}
        departureMemberPricingByCategory={departureMemberPricingByCategory}
      />

      {!isDepartureSoldOut && isWeddingGiftRegistry && (
        <AddToWeddingGiftRegistryCartCTA
          onClick={() => onHandleDepartureModal(departure)}
        />
      )}
      {!isDepartureSoldOut && !isWeddingGiftRegistry && (
        <TextButton
          kind="primary"
          onClick={() => onHandleDepartureModal(departure)}
        >
          {intl.formatMessage({
            id: 'qfwVLT0qN37wjcCyMAow',
            defaultMessage: 'Select',
            description: 'Button label to select a cruise departure in the cruise section',
          })}
        </TextButton>
      )}

    </Group>
    <Divider kind="secondary" />
  </>
}

export default CruiseDepartureTile
